<script>
import CameraDialog from './CameraDialog'

export default {
  name: 'PhotoPicker',
  components: {
    CameraDialog,
  },
  props: {
    value: {
      type: Array,
      required: true,
    },
    tags: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedPhotos: [],
      showCameraDialog: false,
      attachFilesDialog: false,
      uploading: false,
      maxUploadDimension: 1600,
      minCompressionQuality: 0.45,
      compressionTargetRatio: 0.1,
    }
  },
  computed: {
    photosArr: {
      get() {
        return this.value
      },
      set(v) {
        this.$emit('input', v)
      },
    },
    usedTags() {
      return this.photosArr.map((x) => x.tag)
    },
  },
  methods: {
    async attachPhotosFromFile() {
      if (this.selectedPhotos.length === 0) {
        return
      }

      this.uploading = true
      const filesToUpload = [...this.selectedPhotos]

      try {
        const compressedPhotos = await Promise.all(
          filesToUpload.map((photo) => this.addFilePhoto(photo))
        )
        const validPhotos = compressedPhotos.filter(Boolean)

        validPhotos.forEach((photo) => this.addPhoto(photo.photo))
        this.attachFilesDialog = false
        this.selectedPhotos = []

        const originalBytes = validPhotos.reduce(
          (total, photo) => total + photo.originalSize,
          0
        )
        const compressedBytes = validPhotos.reduce(
          (total, photo) => total + photo.compressedSize,
          0
        )
        const savedRatio =
          originalBytes > 0
            ? Math.max(
                0,
                Math.round(
                  ((originalBytes - compressedBytes) / originalBytes) * 100
                )
              )
            : 0

        this.$store.commit('notifications/addMessage', {
          message: `${validPhotos.length} photo${
            validPhotos.length === 1 ? '' : 's'
          } added${savedRatio > 0 ? ` (${savedRatio}% smaller)` : ''}`,
        })
      } catch (err) {
        this.$store.commit('notifications/addMessage', {
          message: err.message || 'Unable to prepare photos for upload',
        })
      } finally {
        this.uploading = false
      }
    },
    getBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
      })
    },
    getScaledDimensions(width, height) {
      const largestDimension = Math.max(width, height)
      if (largestDimension <= this.maxUploadDimension) {
        return { width, height }
      }

      const scaleFactor = this.maxUploadDimension / largestDimension
      return {
        width: Math.max(1, Math.round(width * scaleFactor)),
        height: Math.max(1, Math.round(height * scaleFactor)),
      }
    },
    loadImage(file) {
      return new Promise((resolve, reject) => {
        const image = new Image()
        const objectUrl = URL.createObjectURL(file)

        image.onload = () => {
          URL.revokeObjectURL(objectUrl)
          resolve(image)
        }
        image.onerror = () => {
          URL.revokeObjectURL(objectUrl)
          reject(new Error(`Unable to read ${file.name}`))
        }

        image.src = objectUrl
      })
    },
    canvasToBlob(canvas, mimeType, quality) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob)
              return
            }

            reject(new Error('Unable to compress selected photo'))
          },
          mimeType,
          quality
        )
      })
    },
    async compressPhoto(photo) {
      if (!photo.type.startsWith('image/')) {
        return {
          originalSize: photo.size,
          compressedSize: photo.size,
          photo: await this.getBase64(photo),
        }
      }

      const image = await this.loadImage(photo)
      const mimeType = 'image/jpeg'
      const targetSize = Math.max(
        Math.round(photo.size * this.compressionTargetRatio),
        120 * 1024
      )
      let { width, height } = this.getScaledDimensions(
        image.width,
        image.height
      )
      let quality = 0.82
      let resultBlob = photo

      while (true) {
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        const context = canvas.getContext('2d')
        context.fillStyle = '#ffffff'
        context.fillRect(0, 0, width, height)
        context.drawImage(image, 0, 0, width, height)

        resultBlob = await this.canvasToBlob(canvas, mimeType, quality)
        if (
          resultBlob.size <= targetSize ||
          (quality <= this.minCompressionQuality &&
            Math.max(width, height) <= 1280)
        ) {
          break
        }

        if (quality > this.minCompressionQuality) {
          quality = Math.max(this.minCompressionQuality, quality - 0.08)
        } else {
          width = Math.max(1, Math.round(width * 0.85))
          height = Math.max(1, Math.round(height * 0.85))
        }
      }

      const outputBlob = resultBlob.size < photo.size ? resultBlob : photo

      return {
        originalSize: photo.size,
        compressedSize: outputBlob.size,
        photo: await this.getBase64(outputBlob),
      }
    },
    addFilePhoto(photo) {
      return this.compressPhoto(photo).catch((err) => {
        throw new Error(err.message || `Unable to add ${photo.name}`)
      })
    },
    addDropFiles(e) {},
    generateTag() {
      let random
      do {
        random = Math.floor(Math.random() * (999 - 100)) + 100
      } while (
        this.usedTags.length > 0 &&
        this.usedTags.find((e) => e === random)
      )
      return random
    },
    addPhoto(photo) {
      if (this.tags) {
        this.photosArr.push({ photo, tag: this.generateTag() })
      } else {
        this.photosArr.push({ photo })
      }
      this.uploading = false
    },
    deletePhoto(index) {
      if (window.confirm('Are you sure you want to delete this photo?')) {
        this.photosArr.splice(index, 1)
      }
    },
  },
}
</script>

<template>
  <div id="photo-picker">
    <CameraDialog v-model="showCameraDialog" @photo="addPhoto" />
    <v-dialog v-model="attachFilesDialog" :persistent="uploading" width="600">
      <v-card>
        <v-toolbar>
          <v-btn :disabled="uploading" icon @click="attachFilesDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-progress-linear
            v-if="uploading"
            :active="uploading"
            :indeterminate="uploading"
            absolute
            bottom
            color="primary accent-4"
          />
          <v-toolbar-title>Attach Photos</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-file-input
            v-model="selectedPhotos"
            :loading="uploading"
            accept=".jpg,.png,.jpeg;capture=camera"
            class="ma-3"
            label="Attach Photos"
            hint="Accepts .png, .jpg, .jpeg"
            persistent-hint
            prepend-icon
            outlined
            multiple
            show-size
          />
          <div class="mx-3 text-caption grey--text text--darken-1">
            Selected photos are automatically optimized before upload to reduce
            file size by up to 90%.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
            :disabled="uploading"
            color="primary"
            text
            @click="attachFilesDialog = false"
          >
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            :disabled="selectedPhotos.length === 0 || uploading"
            :loading="uploading"
            color="primary"
            @click="attachPhotosFromFile"
          >
            Add Photos
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card outlined>
      <v-toolbar>
        <v-icon class="mr-5"> mdi-camera-burst </v-icon>
        <v-toolbar-title class="d-none d-md-block">
          Photos Picker
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn text color="primary" @click="attachFilesDialog = true">
            {{ $vuetify.breakpoint.mobile ? 'Gallery' : 'Attach Photos' }}
          </v-btn>
          <v-btn text color="primary" @click="showCameraDialog = true">
            Camera
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text
        style="background-color: #fafafa"
        @drop.prevent.stop="addDropFiles"
      >
        <div v-if="photosArr.length === 0" class="text-center">
          <h3>No Photos</h3>
        </div>
        <v-row v-else>
          <v-col
            v-for="(photoObject, index) in photosArr"
            :key="index"
            class="d-flex child-flex"
            cols="12"
            md="3"
            lg="2"
          >
            <v-card>
              <v-card-text>
                <v-img :src="photoObject.photo" />
              </v-card-text>
              <v-card-actions>
                <v-chip v-if="tags" color="blue" class="white--text" label>
                  #{{ photoObject.tag }}
                </v-chip>
                <v-spacer />
                <v-btn icon color="red" @click="deletePhoto(index)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>
